import {
  ArrowTrendingUpIcon,
  BuildingLibraryIcon,
  CodeBracketIcon,
  CommandLineIcon,
  CpuChipIcon,
  RocketLaunchIcon,
  StarIcon
} from '@heroicons/react/24/solid'
import type React from 'react'
import { JavaScriptLogo } from '../../../../svg/logos/index'
import { BinaryTreeLogo } from '../../../logos/BinaryTreeLogo'
import { OIJLogo } from '../../../logos/OIJLogo'
import { OILogo } from '../../../logos/OILogo'

export type Goal = {
  name: string
  description: string
  href: string
  icon: React.ReactNode
}

export const aboutLinks = [
  {
    name: 'O nas',
    href: '/o-nas'
  },
  {
    name: 'Czym jest mentoring?',
    href: '/czym-jest-mentoring'
  },
  {
    name: 'Aktualności',
    href: '/aktualnosci'
  },
  {
    name: 'Nasza metoda nauczania',
    href: '/nasza-metoda-nauczania'
  },
  {
    name: 'Nasi partnerzy',
    href: '/partnerzy'
  },
  {
    name: 'Program stypendialny',
    href: '/stypendium'
  },
  {
    name: 'Najczęściej zadawane pytania',
    href: '/faq'
  },
  {
    name: 'Nasza oferta mentoringu',
    href: '/mentoring'
  }
]

export const mentoringLinks = [
  {
    name: 'Oferta',
    href: '/mentoring'
  }
]

export const manageSiteLinks = [
  {
    name: 'Portfolio nauczycieli',
    href: '/portfolia'
  },
  {
    name: 'Technologie i umiejętności',
    href: '/technologie/zarzadzaj'
  }
]

// SYNC: these slugs must match apps/algoacademy/src/app/sitemap.ts
// NOTE: order is interleaved for the row-major `grid-cols-2` menu (OfertaOptions.tsx):
// indices [0,2,4] fill the left column, [1,3,5] fill the right column (per issue #747).
export const mentoringPodstawowyGoals: Goal[] = [
  {
    name: 'Cel: Pierwsze kroki w programowaniu',
    description: 'Zacznij programować od zera i opanuj podstawy algorytmów',
    href: '/oferta/mentoring-podstawowy/zacznij-programowac',
    icon: <JavaScriptLogo size={24} />
  },
  {
    name: 'Cel: Topowe studia informatyczne w Polsce',
    description: 'Dostań się na najlepsze uczelnie techniczne w Polsce',
    href: '/oferta/mentoring-podstawowy/topowe-studia-pl',
    icon: <BuildingLibraryIcon className='h-6 w-6' />
  },
  {
    name: 'Cel: Finalista Olimpiady Informatycznej Juniorów',
    description: 'Trenuj systematycznie i dotrzyj do finału OIJ',
    href: '/oferta/mentoring-podstawowy/finalista-oij',
    icon: <OIJLogo />
  },
  {
    name: 'Cel: Pierwsza praca w IT',
    description: 'Przygotuj się do rekrutacji i zacznij karierę jako developer',
    href: '/oferta/mentoring-podstawowy/pierwsza-praca-w-it',
    icon: <BinaryTreeLogo />
  },
  {
    name: 'Cel: Finalista Olimpiady Informatycznej',
    description: 'Trenuj intensywnie i stań się finalistą OI',
    href: '/oferta/mentoring-podstawowy/finalista-oi',
    icon: <OILogo />
  },
  {
    name: 'Cel: AI w praktyce',
    description: 'Naucz się stosować AI w projektach i codziennej pracy',
    href: '/oferta/mentoring-podstawowy/wdrazaj-ai',
    icon: <CpuChipIcon className='h-6 w-6' />
  }
]

// NOTE: interleaved for the row-major `grid-cols-2` menu (per issue #747).
export const mentoringSpersonalizowanyGoals: Goal[] = [
  {
    name: 'Cel: Międzynarodowa Olimpiada Informatyczna Juniorów',
    description: 'Reprezentuj Polskę na Międzynarodowej Olimpiadzie Juniorów',
    href: '/oferta/mentoring-spersonalizowany/ioi-junior',
    icon: <OIJLogo />
  },
  {
    name: 'Cel: Programista ery AI',
    description: 'Twórz oprogramowanie szybciej, łącząc swoje umiejętności z AI',
    href: '/oferta/mentoring-spersonalizowany/programista-ery-ai',
    icon: <CommandLineIcon className='h-6 w-6' />
  },
  {
    name: 'Cel: Międzynarodowa Olimpiada Informatyczna',
    description: 'Reprezentuj Polskę na Międzynarodowej Olimpiadzie Informatycznej',
    href: '/oferta/mentoring-spersonalizowany/ioi',
    icon: <OILogo />
  },
  {
    name: 'Cel: Awans w pracy',
    description: 'Rozwiń kompetencje techniczne i zrób kolejny krok w karierze',
    href: '/oferta/mentoring-spersonalizowany/awans-w-pracy',
    icon: <ArrowTrendingUpIcon className='h-6 w-6' />
  },
  {
    name: 'Cel: Topowe studia zagraniczne',
    description: 'Aplikuj na ETH Zurich, MIT, Oxford i inne najlepsze światowe uczelnie',
    href: '/oferta/mentoring-spersonalizowany/studia-zagraniczne',
    icon: <BuildingLibraryIcon className='h-6 w-6' />
  },
  {
    name: 'Cel: Wybierz własny cel',
    description: 'Zdefiniuj swój cel i osiągnij go z dedykowanym mentorem',
    href: '/oferta/mentoring-spersonalizowany/wlasny-cel',
    icon: <StarIcon className='h-6 w-6' />
  }
]

// NOTE: interleaved for the row-major `grid-cols-2` menu (per issue #747).
export const mentoringProjektowyGoals: Goal[] = [
  {
    name: 'Cel: Startup technologiczny',
    description: 'Zbuduj i rozwiń swój startup technologiczny z pomocą mentora',
    href: '/oferta/mentoring-projektowy/startup',
    icon: <RocketLaunchIcon className='h-6 w-6' />
  },
  {
    name: 'Cel: Własny projekt',
    description: 'Buduj i rozwijaj swój projekt - od pomysłu do wdrożenia',
    href: '/oferta/mentoring-projektowy/wlasny-projekt',
    icon: <CodeBracketIcon className='h-6 w-6' />
  },
  {
    name: 'Cel: AI w Twoim biznesie',
    description: 'Wdróż AI, które udoskonali Twoją działalność',
    href: '/oferta/mentoring-projektowy/ai-w-biznesie',
    icon: <CpuChipIcon className='h-6 w-6' />
  },
  {
    name: 'Cel: Wybierz własny cel',
    description: 'Zdefiniuj projekt i zrealizuj go z pomocą mentora',
    href: '/oferta/mentoring-projektowy/wlasny-cel',
    icon: <StarIcon className='h-6 w-6' />
  }
]
